import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Button } from '../Button';
import { TrajectoryChart } from '../chart/TrajectoryChart';
import { results } from '../../data/study';
import { useLayout } from '../../contexts/LayoutContext';
import { EASE } from '../../utils/motion';
type ResultScreenProps = {
  estimate: number;
  onNext: () => void;
};
export function ResultScreen({
  estimate,
  onNext
}: ResultScreenProps) {
  const reduce = useReducedMotion();
  const {
    compact,
    wide
  } = useLayout();
  const fade = (delay: number) => ({
    initial: reduce ? false : {
      opacity: 0,
      y: 8
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.28,
      delay: reduce ? 0 : delay,
      ease: EASE
    }
  });
  const label = <motion.p {...fade(0)} className="text-xs text-muted" data-id="element-155" data-mp="23c9dt9jz6jd4">
      Week 52 · Change in FVC from baseline
    </motion.p>;
  const numbers = <div data-id="element-156" data-mp="rzgm1yr6pux2">
      <motion.h2 {...fade(1.85)} className={`font-display font-light leading-none tracking-[-0.045em] tabular-nums text-accent ${compact ? 'text-[5rem]' : 'text-[8.5rem]'}`} data-id="element-157" data-mp="rz75f1s15041">
        {results.difference}
        <span className="ml-2 text-[0.32em] text-accent/80" data-id="element-158" data-mp="2bb2nj32qi6fo">mL</span>
      </motion.h2>
      <motion.p {...fade(1.95)} className={`mt-4 max-w-xs leading-relaxed text-ink ${compact ? 'text-base' : 'text-lg'}`} data-id="element-159" data-mp="2pa2b07eigfa9">
        Adjusted difference in FVC decline at Week&nbsp;52
      </motion.p>
      <motion.dl {...fade(2.1)} className={`grid max-w-xs grid-cols-2 border-t border-hairline pt-5 ${compact ? 'mt-6' : 'mt-10'}`} data-id="element-160" data-mp="2y7mdtks0ujmz">
        <div data-id="element-161" data-mp="37zs6kwq3l538">
          <dt className="text-xs text-muted" data-id="element-162" data-mp="37y7a31k83c2q">Your estimate</dt>
          <dd className="mt-1 text-xl tabular-nums text-ink" data-id="element-163" data-mp="3305u4pi3fsvh">{estimate} mL</dd>
        </div>
        <div data-id="element-164" data-mp="3hxg05jbyw6jt">
          <dt className="text-xs text-muted" data-id="element-165" data-mp="3hx6h6fpcaznm">Observed</dt>
          <dd className="mt-1 text-xl tabular-nums text-accent" data-id="element-166" data-mp="3hxocna46h9uh">{results.difference} mL</dd>
        </div>
      </motion.dl>
    </div>;
  const cta = <motion.div {...fade(2.2)} data-id="element-167" data-mp="3k2b7xv50c88w">
      <Button fullWidth={compact} onClick={onNext} data-id="element-168" data-mp="3k34kxduk7tzh">
        Put it in context
      </Button>
    </motion.div>;
  if (wide) {
    return <div className="grid min-h-full grid-cols-[minmax(0,4fr)_minmax(0,7fr)] items-center gap-16 py-10" data-id="element-169" data-mp="3tzzgbnvjde9j">
        <div data-id="element-170" data-mp="2ks3wljx0hzc">
          {label}
          <div className="mt-4" data-id="element-171" data-mp="2ltpvl2gyovu">{numbers}</div>
          <div className="mt-10" data-id="element-172" data-mp="7k4yxtkxwvd0">{cta}</div>
        </div>
        <TrajectoryChart animated prediction={estimate} data-id="element-173" data-mp="7jd5aoawl7pd" />
      </div>;
  }
  return <div className={`flex min-h-full flex-col ${compact ? 'py-6' : 'justify-center py-10'}`} data-id="element-174" data-mp="bkorskyhdctc">
      {label}
      <div className={compact ? 'mt-5' : 'mt-8'} data-id="element-175" data-mp="gm4dmf8hfncs">
        <TrajectoryChart animated prediction={estimate} data-id="element-176" data-mp="ljnulmwkee8f" />
      </div>
      <div className={compact ? 'mt-8' : 'mt-10'} data-id="element-177" data-mp="lhtjt1f0c2i2">{numbers}</div>
      <div className={compact ? 'mt-auto pt-8' : 'mt-10'} data-id="element-178" data-mp="2id7sjgpeno35">{cta}</div>
    </div>;
}